/**
 * AdminUserDetailModal - Support view of a single user (opened from AdminUsersTab).
 *
 * Tabs: Acesso & Plano (diagnostics), Perfil & Instagram, Conta (e-mail/senha),
 * Auditoria. Every action is admin-only and audited server-side.
 * The header also has "Entrar como usuário" (support impersonation, new tab).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { X, History } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAdminUserDetail, type AdminUserDetail } from '@/services/adminService';
import AccessPanel from './user-detail/AccessPanel';
import ProfilePanel from './user-detail/ProfilePanel';
import AccountPanel from './user-detail/AccountPanel';
import ImpersonateAction from './user-detail/ImpersonateAction';
import { AuditLogList, Badge, FeedbackBanner, Section, formatDate, useFeedback } from './user-detail/shared';

type DetailTab = 'access' | 'profile' | 'account' | 'audit';

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'access', label: 'Acesso & Plano' },
  { id: 'profile', label: 'Perfil & Instagram' },
  { id: 'account', label: 'Conta' },
  { id: 'audit', label: 'Auditoria' },
];

interface AdminUserDetailModalProps {
  userId: string | null;
  onClose: () => void;
  /** Called after any successful action so the users list can refresh. */
  onUserChanged?: () => void;
}

export default function AdminUserDetailModal({ userId, onClose, onUserChanged }: AdminUserDetailModalProps) {
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState<DetailTab>('access');
  const feedback = useFeedback();

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadError('');
    try {
      setDetail(await getAdminUserDetail(userId));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Erro ao carregar usuário');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setDetail(null);
    setActiveTab('access');
    feedback.clear();
    load();
  }, [load, feedback.clear]);

  const handleChanged = useCallback(() => {
    load();
    onUserChanged?.();
  }, [load, onUserChanged]);

  if (!userId) return null;

  const profile = detail?.profile;
  const highAlerts = detail?.diagnosticos.filter((d) => d.severidade === 'alta').length ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-background rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-background z-10 px-6 pt-4 border-b border-border">
          <div className="flex items-start gap-4">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/10" />
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-foreground break-words">
                {profile?.display_name || (loading ? 'Carregando…' : 'Sem nome')}
              </h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                <span>{detail?.auth.email}</span>
                {profile?.handle && <span>@{profile.handle}</span>}
                {profile?.role === 'admin' && <Badge label="admin" className="bg-primary/10 text-primary" />}
                {highAlerts > 0 && <Badge label={`${highAlerts} alerta(s)`} className="bg-red-100 text-red-800" />}
                {detail?.auth.must_change_password && (
                  <Badge label="Troca de senha pendente" className="bg-yellow-100 text-yellow-800" />
                )}
              </div>
              {detail && <div className="text-xs text-muted-foreground mt-0.5">Criado em {formatDate(detail.auth.created_at)}</div>}
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {detail && <ImpersonateAction detail={detail} feedback={feedback} onChanged={handleChanged} />}

          <div className="flex gap-1 mt-3 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          <FeedbackBanner feedback={feedback.feedback} onClose={feedback.clear} />

          {loadError && <div className="py-8 text-center text-destructive text-sm">{loadError}</div>}
          {!detail && loading && <div className="py-12 text-center text-muted-foreground">Carregando...</div>}

          {/* Panels stay mounted while reloading so one-time values (temporary password) are not lost. */}
          {detail && (
            <>
              {activeTab === 'access' && <AccessPanel detail={detail} feedback={feedback} onChanged={handleChanged} />}
              {activeTab === 'profile' && <ProfilePanel detail={detail} feedback={feedback} onChanged={handleChanged} />}
              {activeTab === 'account' && <AccountPanel detail={detail} feedback={feedback} onChanged={handleChanged} />}
              {activeTab === 'audit' && (
                <Section title="Últimas 20 ações" icon={<History className="h-4 w-4 text-muted-foreground" />}>
                  <AuditLogList logs={detail.auditLogs} />
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
